import React, { useEffect, useState } from 'react';
import { Container, Card, Badge, Button, Spinner, Alert, Row, Col, Image, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../config/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faInfoCircle, faExclamationTriangle, faImage, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import '../styles/ReturnRequests.css';

const ReturnRequests = () => {
    const [returnRequests, setReturnRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImage, setSelectedImage] = useState('');
    const [cancellingId, setCancellingId] = useState(null);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [selectedRequestId, setSelectedRequestId] = useState(null);
    const navigate = useNavigate();
    const { user } = useAuth();

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }

        fetchReturnRequests();
    }, [user, navigate]);

    const fetchReturnRequests = async () => {
        try {
            setLoading(true);
            const response = await api.get('/returns/user');
            setReturnRequests(response.data);
        } catch (err) {
            console.error('İade talepleri getirme hatası:', err);
            setError(err.response?.data?.error || 'İade talepleriniz getirilemedi');
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'pending':
                return <Badge bg="warning">İnceleniyor</Badge>;
            case 'approved':
                return <Badge bg="success">Onaylandı</Badge>;
            case 'rejected':
                return <Badge bg="danger">Reddedildi</Badge>;
            case 'completed':
                return <Badge bg="info">Tamamlandı</Badge>;
            case 'cancelled':
                return <Badge bg="secondary">İptal Edildi</Badge>;
            default:
                return <Badge bg="secondary">Bilinmiyor</Badge>;
        }
    };

    const formatDate = (dateString) => {
        const options = { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        };
        return new Date(dateString).toLocaleDateString('tr-TR', options);
    };

    // İade nedenini Türkçe'ye çeviren fonksiyon
    const getReasonText = (reasonCode) => {
        switch (reasonCode) {
            case 'wrong_product':
                return 'Yanlış ürün gönderildi';
            case 'damaged':
                return 'Ürün hasarlı/arızalı geldi';
            case 'not_as_described':
                return 'Ürün açıklamadaki gibi değil';
            case 'size_issue':
                return 'Ürün boyutu/ölçüsü uygun değil';
            case 'changed_mind':
                return 'Fikrim değişti';
            case 'other':
                return 'Diğer';
            default:
                return reasonCode;
        }
    };

    const handleImageClick = (imageUrl) => {
        setSelectedImage(imageUrl);
        setShowImageModal(true);
    };

    const closeImageModal = () => {
        setShowImageModal(false);
        setSelectedImage('');
    };

    const handleCancelRequest = (requestId) => {
        setSelectedRequestId(requestId);
        setShowCancelModal(true);
    };

    const confirmCancelRequest = async () => {
        if (!selectedRequestId) return;
        
        try {
            setCancellingId(selectedRequestId);
            const response = await api.delete(`/returns/${selectedRequestId}`);
            
            if (response.status === 200) {
                // Update the local state to reflect the cancellation
                setReturnRequests(prevRequests => 
                    prevRequests.map(request => 
                        request.id === selectedRequestId 
                            ? { ...request, status: 'cancelled' } 
                            : request
                    )
                );
                
                setShowCancelModal(false);
                alert('İade talebiniz başarıyla iptal edildi.');
            }
        } catch (err) {
            console.error('İade talebi iptal hatası:', err);
            alert(err.response?.data?.error || 'İade talebi iptal edilirken bir hata oluştu');
        } finally {
            setCancellingId(null);
        }
    };

    if (loading) {
        return (
            <Container className="return-requests-container text-center py-5">
                <Spinner animation="border" role="status" variant="primary" />
                <p className="mt-3">İade talepleriniz yükleniyor...</p>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="return-requests-container py-5">
                <Alert variant="danger">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    {error}
                </Alert>
                <div className="text-center mt-4">
                    <Button 
                        variant="outline-primary" 
                        onClick={() => navigate('/profile')}
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                        Hesabıma Dön
                    </Button>
                </div>
            </Container>
        );
    }

    return (
        <Container className="return-requests-container py-4">
            <div className="return-requests-header d-flex justify-content-between align-items-center mb-4">
                <h2>İade Taleplerim</h2>
                <Button 
                    variant="outline-primary" 
                    onClick={() => navigate('/profile')}
                >
                    <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                    Hesabıma Dön
                </Button>
            </div>

            {returnRequests.length === 0 ? (
                <div className="no-returns text-center py-5">
                    <FontAwesomeIcon icon={faInfoCircle} className="no-returns-icon mb-3" />
                    <h4>Henüz iade talebiniz bulunmamaktadır</h4>
                    <p className="text-muted">Siparişleriniz için iade talebi oluşturmak için "Siparişlerim" sayfasını ziyaret edebilirsiniz.</p>
                    <Button 
                        variant="primary" 
                        onClick={() => navigate('/profile')}
                        className="mt-3"
                    >
                        Siparişlerime Git
                    </Button>
                </div>
            ) : (
                <div className="return-requests-list">
                    {returnRequests.map(request => (
                        <Card key={request.id} className="return-request-card mb-4">
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <div>
                                    <span className="order-number">Sipariş No: {request.order_number}</span>
                                </div>
                                <div>
                                    {getStatusBadge(request.status)}
                                </div>
                            </Card.Header>
                            <Card.Body>
                                <div className="return-request-details">
                                    <div className="row">
                                        <div className="col-md-6">
                                            <p><strong>Talep Tarihi:</strong> {formatDate(request.created_at)}</p>
                                            <p><strong>İade Nedeni:</strong> {getReasonText(request.reason)}</p>
                                        </div>
                                        <div className="col-md-6">
                                            <p><strong>Son Güncelleme:</strong> {formatDate(request.updated_at || request.created_at)}</p>
                                            {request.status === 'approved' && request.customer_instructions && (
                                                <div className="return-instructions alert alert-info">
                                                    <p className="mb-0">
                                                        <strong>İade Talimatları:</strong> {request.customer_instructions}
                                                    </p>
                                                </div>
                                            )}
                                            {request.status === 'approved' && !request.customer_instructions && (
                                                <div className="return-instructions alert alert-info">
                                                    <p className="mb-0">
                                                        <strong>İade Talimatları:</strong> İade talebiniz onaylanmıştır. Lütfen ürünü orijinal ambalajında, fatura ile birlikte aşağıdaki adrese gönderin.
                                                    </p>
                                                    <p className="mb-0 mt-2">
                                                        <small>
                                                            Mr. Brown Kılıf, Atatürk Mah. Ertuğrul Gazi Cad. No:21 Kat:3 Ataşehir/İstanbul
                                                        </small>
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {request.description && (
                                        <div className="customer-description mt-3">
                                            <h6>Açıklamanız:</h6>
                                            <p className="description-text">{request.description}</p>
                                        </div>
                                    )}

                                    {/* Yüklenen görseller */}
                                    {request.images && request.images.length > 0 && (
                                        <div className="return-images mt-3">
                                            <h6>Yüklenen Görseller:</h6>
                                            <Row className="image-gallery">
                                                {request.images.map((imageUrl, index) => (
                                                    <Col key={index} xs={6} md={3} className="mb-3">
                                                        <div 
                                                            className="return-image-thumbnail" 
                                                            onClick={() => handleImageClick(imageUrl)}
                                                        >
                                                            <Image 
                                                                src={imageUrl} 
                                                                thumbnail 
                                                                className="w-100 h-100 object-fit-cover"
                                                                alt={`İade görseli ${index + 1}`}
                                                            />
                                                            <div className="image-overlay">
                                                                <FontAwesomeIcon icon={faImage} />
                                                            </div>
                                                        </div>
                                                    </Col>
                                                ))}
                                            </Row>
                                        </div>
                                    )}

                                    {request.admin_notes && (
                                        <div className="admin-notes mt-3">
                                            <h6>Satıcı Notu:</h6>
                                            <div className="admin-note-box">
                                                {request.admin_notes}
                                            </div>
                                        </div>
                                    )}

                                    {request.status === 'rejected' && (
                                        <div className="rejected-info alert alert-danger mt-3">
                                            <p className="mb-0">
                                                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                                                İade talebiniz reddedilmiştir. Detaylı bilgi için satıcı notunu inceleyebilir veya müşteri hizmetleri ile iletişime geçebilirsiniz.
                                            </p>
                                        </div>
                                    )}

                                    {/* İptal Butonu - Sadece bekleyen talepler için göster */}
                                    {request.status === 'pending' && (
                                        <div className="cancel-request-container mt-3">
                                            <Button 
                                                variant="outline-danger" 
                                                size="sm"
                                                onClick={() => handleCancelRequest(request.id)}
                                                disabled={cancellingId === request.id}
                                                className="cancel-request-button"
                                            >
                                                <FontAwesomeIcon icon={faTrash} className="me-2" />
                                                {cancellingId === request.id ? 'İptal Ediliyor...' : 'İade Talebini İptal Et'}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </Card.Body>
                            <Card.Footer className="text-muted">
                                <small>Talep ID: {request.id.substring(0, 8)}...</small>
                            </Card.Footer>
                        </Card>
                    ))}
                </div>
            )}

            {/* Görsel Önizleme Modalı */}
            <Modal show={showImageModal} onHide={closeImageModal} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Görsel Önizleme</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    <Image src={selectedImage} fluid alt="İade görseli" className="modal-image" />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeImageModal}>
                        Kapat
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* İptal Onay Modalı */}
            <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>İade Talebini İptal Et</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>İade talebinizi iptal etmek istediğinize emin misiniz?</p>
                    <p className="text-muted">Bu işlem geri alınamaz. İptal edilen iade talebi için yeni bir talep oluşturabilirsiniz.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCancelModal(false)}>
                        Vazgeç
                    </Button>
                    <Button 
                        variant="danger" 
                        onClick={confirmCancelRequest}
                        disabled={cancellingId !== null}
                    >
                        {cancellingId !== null ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                İptal Ediliyor...
                            </>
                        ) : 'İptal Et'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default ReturnRequests; 